import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ClickAwayListener, IconButton, styled } from '@mui/material'
import { ColDef } from 'ag-grid-community'
import { intl } from '../../../../../i18n'
import { Color, FontSize, IconColor } from '../../../../../styles/commonStyles'
import { colorPalette } from '../../../../style/colorPalette'
import FilterPopper from './FilterPopper'
import FilterIcon from '../../../../components/icons/FilterIcon'
import { BootstrapTooltip } from '../../../../components/tooltips/Tooltip/BootStrapToopTip'

type Props = {
  filteredColumns: ColDef[]
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters: () => void
  getDisplayColumnName?: (colDef: ColDef) => string | undefined
}

const StyledIconButton = styled(IconButton)<{ active: boolean }>(
  ({ active: hasFilters }) => ({
    fontSize: FontSize.MEDIUM,
    fontWeight: 500,
    display: 'flex',
    gap: '3px',
    padding: '5px 10px',
    cursor: hasFilters ? 'pointer' : 'default',
  })
)

const StyledFilterIcon = styled(FilterIcon)({
  width: '13px',
  height: '13px',
  color: colorPalette.monotone[4],
  verticalAlign: 'middle',
})

const StyledSeparator = styled('span')({
  color: colorPalette.monotone[3],
})

const StyledFilterCount = styled('span')<{ active: boolean }>(({ active }) => ({
  color: active ? colorPalette.skyBlue[7] : colorPalette.monotone[3],
}))

const FilterButton = ({
  filteredColumns,
  onDeleteFilteredColumn,
  resetFilters,
  getDisplayColumnName,
}: Props) => {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const [mouseOver, setMouseOver] = useState(false)

  const hasFilters = useMemo(
    () => filteredColumns.length >= 1,
    [filteredColumns]
  )
  const handleClose = useCallback(() => setIsPopperOpen(false), [])
  const handleClick = useCallback(
    () =>
      setIsPopperOpen(prev => {
        if (!hasFilters) return false
        return !prev
      }),
    [hasFilters]
  )

  const handleMouseEnter = useCallback(() => setMouseOver(true), [])
  const handleMouseLeave = useCallback(() => setMouseOver(false), [])

  const isTooltipOpen = useMemo(
    () => mouseOver && !isPopperOpen,
    [mouseOver, isPopperOpen]
  )

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <div style={{ minWidth: 'fit-content' }}>
          <BootstrapTooltip
            arrow={true}
            title={intl.formatMessage({
              id: 'wbs.header.filtered.table.column.count',
            })}
            open={isTooltipOpen}
            onClose={() => setMouseOver(false)}
            disableInteractive={true}
          >
            <StyledIconButton
              ref={anchorRef}
              disableRipple={true}
              active={hasFilters}
              onClick={handleClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <StyledFilterIcon />
              <StyledFilterCount active={hasFilters}>
                <StyledSeparator>
                  {intl.formatMessage({
                    id: 'wbs.header.filter.sort.label.separator',
                  })}
                </StyledSeparator>
                {hasFilters
                  ? filteredColumns.length
                  : intl.formatMessage({ id: 'wbs.header.none.label' })}
              </StyledFilterCount>
            </StyledIconButton>
          </BootstrapTooltip>
        </div>
      </ClickAwayListener>
      <FilterPopper
        open={isPopperOpen}
        anchorEl={anchorRef.current}
        filteredColumns={filteredColumns}
        placement={'bottom-start'}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        getDisplayColumnName={getDisplayColumnName}
      />
    </>
  )
}
export default FilterButton
